/**
 * SOLEÁ — interações da vitrine.
 *
 * - entrada suave de seções no scroll;
 * - carrossel com setas e barra de progresso (a rolagem é nativa, com snap);
 * - compra rápida nos cards, usando o mesmo evento do formulário de produto
 *   do tema, para o carrinho lateral abrir e atualizar sozinho;
 * - guia "Encontre a sua", que monta o link para uma coleção real.
 *
 * Sem dependências. Carregado como módulo, depois do HTML.
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

window.SoleaReady = true;

/* `Theme` é declarado com const num script clássico do tema: existe no escopo
   global, mas não como propriedade de window. */
const routes = (typeof Theme !== 'undefined' && Theme.routes) || {};

/* ------------------------------------------------------------------------ */
/* Entrada no scroll                                                         */
/* ------------------------------------------------------------------------ */

const revealObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries, observer) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
      )
    : null;

function observeReveals(root = document) {
  const items = root.querySelectorAll('[data-solea-reveal]:not(.is-visible)');
  if (!revealObserver || !document.documentElement.classList.contains('solea-motion')) {
    items.forEach((item) => item.classList.add('is-visible'));
    return;
  }
  items.forEach((item) => revealObserver.observe(item));
}

observeReveals();
document.addEventListener('shopify:section:load', (event) => observeReveals(event.target));

/* ------------------------------------------------------------------------ */
/* Carrossel                                                                 */
/* ------------------------------------------------------------------------ */

class SoleaCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-solea-track]');
    this.prev = this.querySelector('[data-solea-prev]');
    this.next = this.querySelector('[data-solea-next]');
    this.nav = this.querySelector('[data-solea-nav]');
    this.progress = this.querySelector('[data-solea-progress]');
    if (!this.track) return;

    this.update = this.update.bind(this);
    this.prev?.addEventListener('click', () => this.go(-1));
    this.next?.addEventListener('click', () => this.go(1));
    this.track.addEventListener('scroll', this.update, { passive: true });
    this.resizeObserver = new ResizeObserver(this.update);
    this.resizeObserver.observe(this.track);
    this.update();
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }

  go(direction) {
    const item = this.track.firstElementChild;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const step = item ? item.getBoundingClientRect().width + gap : this.track.clientWidth;
    const perPage = Math.max(1, Math.floor((this.track.clientWidth + gap) / step));
    this.track.scrollBy({ left: direction * step * perPage, behavior: 'smooth' });
  }

  update() {
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    const overflow = scrollWidth - clientWidth;
    const scrollable = overflow > 4;

    if (this.nav) this.nav.hidden = !scrollable;
    if (this.prev) this.prev.disabled = scrollLeft <= 4;
    if (this.next) this.next.disabled = scrollLeft >= overflow - 4;

    if (this.progress) {
      this.progress.hidden = !scrollable;
      const size = scrollable ? (clientWidth / scrollWidth) * 100 : 100;
      const position = scrollable ? (scrollLeft / overflow) * (100 / size - 1) * 100 : 0;
      this.progress.style.setProperty('--solea-progress-size', `${size}%`);
      this.progress.style.setProperty('--solea-progress-x', `${position}%`);
    }
  }
}

if (!customElements.get('solea-carousel')) customElements.define('solea-carousel', SoleaCarousel);

/* ------------------------------------------------------------------------ */
/* Compra rápida                                                             */
/* ------------------------------------------------------------------------ */

document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-solea-quick-add')) return;
  event.preventDefault();
  quickAdd(form);
});

async function refreshCart() {
  const response = await fetch(`${routes.cart_url || '/cart'}.json`, {
    headers: { Accept: 'application/json' },
  });
  return response.json();
}

function quickAdd(form) {
  const button = form.querySelector('button[type="submit"]');
  const status = form.querySelector('[data-solea-quick-status]');
  const formData = new FormData(form);

  const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
    .map((element) => element instanceof HTMLElement && element.dataset.sectionId)
    .filter(Boolean);
  if (sectionIds.length) formData.append('sections', sectionIds.join(','));

  const quantity = Number(formData.get('quantity')) || 1;
  const deferred = CartLinesUpdateEvent.createPromise();

  button?.setAttribute('aria-busy', 'true');

  form.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(formData.get('id')), quantity }],
      promise: deferred.promise,
    })
  );

  const config = fetchConfig('javascript', { body: formData });

  fetch(routes.cart_add_url || '/cart/add', {
    ...config,
    headers: { ...config.headers, Accept: 'text/html' },
  })
    .then((response) => response.json())
    .then(async (response) => {
      const cart = await refreshCart();
      const failed = Boolean(response.status);

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'solea-quick-add',
          sourceId: form.id,
          itemCount: quantity,
          productId: form.dataset.productId,
          sections: failed ? undefined : response.sections,
          didError: failed,
        },
      });

      if (failed) {
        form.dispatchEvent(
          new CartErrorEvent({
            error: response.message || 'Não foi possível adicionar',
            code: 'INVALID',
            detail: { description: response.description, errors: response.errors },
          })
        );
        if (status) status.textContent = response.description || response.message || '';
        return;
      }

      if (status) status.textContent = button?.dataset.addedLabel || '';
      if (button) {
        button.dataset.state = 'added';
        window.setTimeout(() => {
          delete button.dataset.state;
          if (status) status.textContent = '';
        }, 2200);
      }
    })
    .catch((error) => {
      deferred.reject(error);
      if (status) status.textContent = button?.dataset.errorLabel || '';
    })
    .finally(() => button?.removeAttribute('aria-busy'));
}

/* ------------------------------------------------------------------------ */
/* Guia "Encontre a sua"                                                     */
/* ------------------------------------------------------------------------ */

class SoleaGuide extends HTMLElement {
  connectedCallback() {
    this.steps = Array.from(this.querySelectorAll('[data-guide-step]'));
    this.result = this.querySelector('[data-guide-result]');
    this.resultLink = this.querySelector('[data-guide-link]');
    this.resultTitle = this.querySelector('[data-guide-result-title]');
    this.giftLink = this.querySelector('[data-guide-gift]');

    this.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (target?.closest('[data-guide-restart]')) {
        this.reset(true);
        return;
      }
      const option = target?.closest('[data-guide-option]');
      if (!(option instanceof HTMLElement)) return;
      // Categorias são links reais (funcionam sem JS); com JS, o guia continua.
      event.preventDefault();
      this.choose(option);
    });

    this.reset(false);
  }

  mark(option, selected) {
    if (option instanceof HTMLButtonElement) option.setAttribute('aria-pressed', String(selected));
    option.classList.toggle('is-selected', selected);
  }

  reset(focus) {
    this.state = { intent: null, category: null, style: null };
    this.querySelectorAll('[data-guide-option]').forEach((option) => this.mark(option, false));
    this.show('intent', focus);
  }

  choose(option) {
    const step = option.closest('[data-guide-step]');
    if (!(step instanceof HTMLElement)) return;
    step.querySelectorAll('[data-guide-option]').forEach((item) => this.mark(item, item === option));

    const kind = step.dataset.guideStep;
    this.state[kind] = { ...option.dataset };

    if (kind === 'intent') {
      this.show('category', true);
      return;
    }

    if (kind === 'category') {
      const styles = (option.dataset.styles || '').split('|').filter(Boolean);
      const styleStep = this.querySelector('[data-guide-step="style"]');
      if (styleStep && styles.length) {
        styleStep.querySelectorAll('[data-guide-option]').forEach((item) => {
          if (item instanceof HTMLElement) item.hidden = !styles.includes(item.dataset.tag || '');
        });
        this.show('style', true);
      } else {
        this.state.style = null;
        this.finish();
      }
      return;
    }

    if (kind === 'style') this.finish();
  }

  show(kind, focus) {
    this.steps.forEach((step) => {
      step.hidden = step.dataset.guideStep !== kind;
    });
    if (this.result) this.result.hidden = true;
    if (!focus) return;
    const current = this.steps.find((step) => step.dataset.guideStep === kind);
    const first = current?.querySelector('[data-guide-option]:not([hidden])');
    if (first instanceof HTMLElement) first.focus({ preventScroll: true });
  }

  finish() {
    const category = this.state.category;
    if (!category || !this.result || !this.resultLink) return;

    let url = category.url || this.resultLink.getAttribute('href') || '/collections/all';
    if (this.state.style?.tag) url = `${url.replace(/\/$/, '')}/${this.state.style.tag}`;
    this.resultLink.setAttribute('href', url);

    if (this.resultTitle) {
      const parts = [category.label];
      if (this.state.style?.label) parts.push(this.state.style.label.toLowerCase());
      this.resultTitle.textContent = parts.filter(Boolean).join(' · ');
    }

    if (this.giftLink) this.giftLink.hidden = this.state.intent?.gift !== 'true';

    this.steps.forEach((step) => {
      step.hidden = true;
    });
    this.result.hidden = false;
    this.resultLink.focus({ preventScroll: true });
  }
}

if (!customElements.get('solea-guide')) customElements.define('solea-guide', SoleaGuide);

/* ------------------------------------------------------------------------ */
/* Carrossel de banners da home                                              */
/* ------------------------------------------------------------------------ */

class SelahSlideshow extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('[data-slides]');
    this.slides = Array.from(this.querySelectorAll('[data-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-dot]'));
    this.live = this.querySelector('[data-live]');
    if (!this.viewport || this.slides.length < 2) return;

    this.index = 0;
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.index - 1));
    this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.index + 1));
    this.dots.forEach((dot) => dot.addEventListener('click', () => this.go(Number(dot.dataset.dot))));

    /* O slide visível manda no estado: serve para arrastar, setas e teclado. */
    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio > 0.6) {
            this.setCurrent(this.slides.indexOf(entry.target));
          }
        }
      },
      { root: this.viewport, threshold: [0.6] }
    );
    this.slides.forEach((slide) => this.observer.observe(slide));

    const interval = Number(this.dataset.autoplay) || 0;
    if (interval && !this.reduced) {
      this.interval = interval;
      this.addEventListener('pointerenter', () => this.stop());
      this.addEventListener('pointerleave', () => this.start());
      this.addEventListener('focusin', () => this.stop());
      this.addEventListener('focusout', () => this.start());
      this.viewport.addEventListener('pointerdown', () => this.stop());
      document.addEventListener('visibilitychange', () => (document.hidden ? this.stop() : this.start()));
      this.pauseObserver = new IntersectionObserver(
        ([entry]) => (entry.isIntersecting ? this.start() : this.stop()),
        { threshold: 0.2 }
      );
      this.pauseObserver.observe(this);
    }
  }

  disconnectedCallback() {
    this.stop();
    this.observer?.disconnect();
    this.pauseObserver?.disconnect();
  }

  setCurrent(index) {
    if (index < 0 || index === this.index) return;
    this.index = index;
    this.dots.forEach((dot, i) => {
      if (i === index) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
    if (this.live) this.live.textContent = `Banner ${index + 1} de ${this.slides.length}`;
  }

  go(index) {
    const total = this.slides.length;
    const target = (index + total) % total;
    this.viewport.scrollTo({ left: this.slides[target].offsetLeft - this.slides[0].offsetLeft, behavior: this.reduced ? 'auto' : 'smooth' });
    this.setCurrent(target);
  }

  start() {
    if (!this.interval || this.timer || document.hidden) return;
    this.timer = window.setInterval(() => this.go(this.index + 1), this.interval);
  }

  stop() {
    window.clearInterval(this.timer);
    this.timer = null;
  }
}

if (!customElements.get('selah-slideshow')) customElements.define('selah-slideshow', SelahSlideshow);
